import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';
import { useEnum, ENUM_NAMES } from '../hooks/useEnum';

interface LabResult {
  id: number;
  pet_id: number;
  pet_name: string;
  owner_name: string;
  test_name: string;
  test_category: string;
  result_value: string;
  reference_range: string;
  units: string;
  status: string;
  is_abnormal: number;
  ordered_by_name: string;
  created_at: string;
}

interface Pet {
  id: number;
  name: string;
}

interface Stats {
  summary: {
    pending_count: number;
    in_progress_count: number;
    completed_count: number;
    abnormal_count: number;
    total_count: number;
  };
  by_category: { test_category: string; count: number }[];
}

const LabResultsPage = () => {
  const [results, setResults] = useState<LabResult[]>([]);
  const [pets, setPets] = useState<Pet[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [deleteResult, setDeleteResult] = useState<LabResult | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { values: labCategories } = useEnum(ENUM_NAMES.LAB_CATEGORIES);
  const { values: labStatuses, getDisplayName: getStatusName } = useEnum(ENUM_NAMES.LAB_STATUSES);
  const [formData, setFormData] = useState({
    pet_id: '',
    test_name: '',
    test_category: 'hematology',
    result_value: '',
    reference_range: '',
    units: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [resultsRes, petsRes, statsRes] = await Promise.all([
        api.get<{ data: LabResult[] }>(API_ENDPOINTS.LAB_RESULTS.LIST),
        api.get<{ data: Pet[] }>(API_ENDPOINTS.PATIENTS.LIST),
        api.get<Stats>(API_ENDPOINTS.LAB_RESULTS.STATS),
      ]);
      setResults(resultsRes.data ?? []);
      setPets(petsRes.data ?? []);
      setStats(statsRes);
    } catch (err) {
      setError('Failed to load lab results');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await api.post(API_ENDPOINTS.LAB_RESULTS.CREATE, {
        ...formData,
        pet_id: parseInt(formData.pet_id),
      });
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      setError('Failed to create lab result');
      console.error(err);
    }
  };

  const handleUpdateStatus = async (id: number, status: string) => {
    try {
      await api.put(`${API_ENDPOINTS.LAB_RESULTS.UPDATE}/${id}`, { status });
      fetchData();
    } catch (err) {
      setError('Failed to update status');
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (!deleteResult) return;
    setDeleting(true);
    try {
      await api.delete(API_ENDPOINTS.LAB_RESULTS.DELETE(deleteResult.id));
      setDeleteResult(null);
      fetchData();
    } catch { setError('Failed to delete'); } finally { setDeleting(false); }
  };

  const resetForm = () => {
    setFormData({ pet_id: '', test_name: '', test_category: 'hematology', result_value: '', reference_range: '', units: '', notes: '' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const filteredResults = results.filter(r => {
    if (filterStatus && r.status !== filterStatus) return false;
    if (filterCategory && r.test_category !== filterCategory) return false;
    return true;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Lab Results</Typography>
      
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Pending</Typography>
              <Typography variant="h4">{stats.summary.pending_count}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">In Progress</Typography>
              <Typography variant="h4">{stats.summary.in_progress_count}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Completed</Typography>
              <Typography variant="h4">{stats.summary.completed_count}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary">Abnormal</Typography>
              <Typography variant="h4" color="error">{stats.summary.abnormal_count}</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          New Lab Result
        </Button>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {labStatuses.map(s => <MenuItem key={s.code} value={s.code}>{s.display_name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Category</InputLabel>
          <Select value={filterCategory} label="Category" onChange={e => setFilterCategory(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {labCategories.map(c => <MenuItem key={c.code} value={c.code}>{c.display_name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Pet</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Test</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Abnormal</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredResults.map(result => (
              <TableRow key={result.id}>
                <TableCell>{result.pet_name}</TableCell>
                <TableCell>{result.owner_name}</TableCell>
                <TableCell>{result.test_name}</TableCell>
                <TableCell>{result.test_category}</TableCell>
                <TableCell>{result.result_value || '-'}</TableCell>
                <TableCell>{result.reference_range || '-'}</TableCell>
                <TableCell>
                  <Chip label={getStatusName(result.status)} color={getStatusColor(result.status) as any} size="small"
                    onClick={() => result.status !== 'completed' && handleUpdateStatus(result.id, result.status === 'pending' ? 'in_progress' : 'completed')} />
                </TableCell>
                <TableCell>
                  {result.is_abnormal ? <Chip label="Yes" color="error" size="small" /> : 'No'}
                </TableCell>
                <TableCell>{new Date(result.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteResult(result)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {filteredResults.length === 0 && (
              <TableRow><TableCell colSpan={10} align="center">No lab results found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Lab Result</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Pet</InputLabel>
            <Select value={formData.pet_id} label="Pet" onChange={e => setFormData({ ...formData, pet_id: e.target.value })}>
              {pets.map(pet => <MenuItem key={pet.id} value={pet.id}>{pet.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth margin="normal" label="Test Name" value={formData.test_name}
            onChange={e => setFormData({ ...formData, test_name: e.target.value })} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Category</InputLabel>
            <Select value={formData.test_category} label="Category" onChange={e => setFormData({ ...formData, test_category: e.target.value })}>
            {labCategories.map(c => <MenuItem key={c.code} value={c.code}>{c.display_name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth margin="normal" label="Result Value" value={formData.result_value}
            onChange={e => setFormData({ ...formData, result_value: e.target.value })} />
          <TextField fullWidth margin="normal" label="Reference Range" value={formData.reference_range}
            onChange={e => setFormData({ ...formData, reference_range: e.target.value })} />
          <TextField fullWidth margin="normal" label="Units" value={formData.units}
            onChange={e => setFormData({ ...formData, units: e.target.value })} />
          <TextField fullWidth margin="normal" label="Notes" multiline rows={3} value={formData.notes}
            onChange={e => setFormData({ ...formData, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!deleteResult} onClose={() => setDeleteResult(null)}>
        <DialogTitle>Delete Lab Result</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteResult?.test_name}</strong> for <strong>{deleteResult?.pet_name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteResult(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LabResultsPage;
